import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Card, Table, Spinner, Pagination, Dropdown } from "react-bootstrap";
import authService from "../../../services/authService";
import { useUser } from "../../../context/UserContext";
import ConfirmationDialog from "./confirmationDialog";

const UserList = ({ users, setUsers }) => {
  const { token } = useUser();
  const [sortConfig, setSortConfig] = useState({ field: "createdAt", direction: "desc" });
  const [loading, setLoading] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [availableNumbers, setAvailableNumbers] = useState([]); // State for available phone numbers
  const [selectedNumber, setSelectedNumber] = useState(""); // State for selected phone number
  const [isFetchingNumbers, setIsFetchingNumbers] = useState(false); // New state for tracking number fetching

  // Handle Confirmation Logic
  const handleActionConfirmation = (action, email) => {
    setSelectedAction(action);
    setSelectedEmail(email);
    setShowConfirmation(true);
    setSelectedNumber(""); // Reset selected number

    // We'll fetch numbers in useEffect
    if (action === "purchaseNumber") {
      setIsFetchingNumbers(true);
    }
  };

  // Use effect to fetch numbers after the modal is shown
  useEffect(() => {
    // Only fetch numbers when the modal is shown and action is purchaseNumber
    if (showConfirmation && selectedAction === "purchaseNumber" && isFetchingNumbers) {
      const fetchNumbers = async () => {
        try {
          setLoading((prev) => ({ ...prev, [selectedEmail]: true }));
          const response = await authService.listAvailableNumbers(selectedEmail, token);
          setAvailableNumbers(response.availableNumbers);
        } catch (error) {
          console.error("Failed to fetch available phone numbers:", error);
          toast.error(error?.message || "Failed to fetch available phone numbers", {
            position: "top-right",
            autoClose: 5000,
          });
        } finally {
          setLoading((prev) => ({ ...prev, [selectedEmail]: false }));
          setIsFetchingNumbers(false);
        }
      };
      
      fetchNumbers();
    }
  }, [showConfirmation, selectedAction, selectedEmail, token, isFetchingNumbers]);

  const handleConfirmAction = async () => {
    setShowConfirmation(false);
    try {
      if (selectedAction === "releaseNumber") {
        await handleReleaseNumber(selectedEmail);
      } else if (selectedAction === "purchaseNumber") {
        if (!selectedNumber) {
          throw new Error("Please select a phone number");
        }
        await handlePurchaseNumber(selectedEmail, selectedNumber);
      } else if (selectedAction === "deleteUser") {
        await handleDeleteUser(selectedEmail);
      }
    } catch (error) {
      console.error("Action failed:", error);
      toast.error(error?.message || "Action failed", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = users.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(users.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      if (sortConfig.field === "createdAt") {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return sortConfig.direction === "asc" 
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      } else if (sortConfig.field === "isActive") {
        return sortConfig.direction === "asc"
          ? (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1)
          : (a.isActive === b.isActive ? 0 : a.isActive ? 1 : -1);
      }
      return 0;
    });
  };

  const handleSort = (field) => {
    setSortConfig((prevConfig) => ({
      field,
      direction: 
        prevConfig.field === field && prevConfig.direction === "asc" 
          ? "desc" 
          : "asc",
    }));
  };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return <img src="/assets/arrow-down-gray.svg" alt="Sort" />;
    }
    return (
      <img 
        src={`/assets/${sortConfig.direction === 'asc' ? 'arrow-up' : 'arrow-down'}-gray.svg`} 
        alt={`Sort ${sortConfig.direction}`} 
      />
    );
  };

  const handleToggleStatus = async (email, isActive) => {
    try {
      setLoading((prev) => ({ ...prev, [email]: true }));

      // Use the authService to update the user's status
      await authService.updateUserStatus(email, !isActive, token);

      // Update the local state to reflect the new status
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.email === email ? { ...user, isActive: !isActive } : user
        )
      );

      // Show success notification
      toast.success(`User status updated successfully!`, {
        position: "top-right",
        autoClose: 5000,
      });
    } catch (error) {
      console.error("Failed to update user status:", error);

      // Show error notification with the backend error message
      const errorMessage = error?.message || "Failed to update user status";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading((prev) => ({ ...prev, [email]: false }));
    }
  };

  const handlePurchaseNumber = async (email, phoneNumber) => {
    try {
      setLoading((prev) => ({ ...prev, [email]: true }));

      // Call the backend API to purchase a phone number
      const response = await authService.purchaseNumber(email, phoneNumber, token);

      // Update the local state to reflect the new phone number
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.email === email ? { ...user, phoneNumber: response.result.phoneNumber, isActive: true } : user
        )
      );

      // Show success notification
      toast.success(`Phone number purchased successfully!`, {
        position: "top-right",
        autoClose: 5000,
      });
    } catch (error) {
      console.error("Failed to purchase phone number:", error);

      // Show error notification with the backend error message
      const errorMessage = error?.message || "Failed to purchase phone number";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading((prev) => ({ ...prev, [email]: false }));
    }
  };

  const handleReleaseNumber = async (email) => {
    try {
      setLoading((prev) => ({ ...prev, [email]: true }));

      // Call the backend API to release the phone number
      await authService.releaseNumber(email, token);

      // Update the local state to remove the phone number
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.email === email ? { ...user, phoneNumber: null, isActive: false } : user
        )
      );

      // Show success notification
      toast.success(`Phone number released successfully!`, {
        position: "top-right",
        autoClose: 5000,
      });
    } catch (error) {
      console.error("Failed to release phone number:", error);

      // Show error notification with the backend error message
      const errorMessage = error?.message || "Failed to release phone number";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading((prev) => ({ ...prev, [email]: false }));
    }
  };

  const handleDeleteUser = async (email) => {
    try {
      setLoading((prev) => ({ ...prev, [email]: true }));

      await authService.deleteUser(email, token);

      // Update the local state to remove the deleted user
      setUsers((prevUsers) => prevUsers.filter((user) => user.email !== email));

      // Show success notification
      toast.success(`User deleted successfully!`, {
        position: "top-right",
        autoClose: 5000,
      });
    } catch (error) {
      console.error("Failed to delete user:", error);

      // Show error notification with the backend error message
      const errorMessage = error?.message || "Failed to delete user";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading((prev) => ({ ...prev, [email]: false }));
    }
  };

  const sortedUsers = sortData(currentItems);

  return (
    <>
      <Card className="shadow-sm border-0 p-4 rounded-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-1">Users</h5>
          </div>
        </div>

        <Table hover className="mt-3">
          <thead>
            <tr className="text-secondary">
              <th>Name</th>
              <th>Email</th>
              <th>Phone Number</th>
              <th onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>
                Date Created {getSortIcon("createdAt")}
              </th>
              <th onClick={() => handleSort("isActive")} style={{ cursor: "pointer" }}>
                Status {getSortIcon("isActive")}
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody className="text-secondary">
            {sortedUsers.map((user) => (
              <tr key={user._id}>
                <td className="text-dark">{`${user.firstName} ${user.lastName}`}</td>
                <td>{user.email}</td>
                <td>
                  {user.phoneNumber ? (
                    <div
                      className="bg-light p-2 d-inline-block"
                      style={{ cursor: "pointer", borderRadius: "0.5rem" }}
                      onClick={() => navigator.clipboard.writeText(user.phoneNumber)}
                      title="Click to copy number"
                    >
                      <span className="text-dark fw-bold">{user.phoneNumber}</span>
                    </div>
                  ) : (
                    <span className="text-muted" />
                  )}
                </td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                <td>
                  <span className={`badge ${user.isActive ? 'bg-success' : 'bg-danger'} bg-opacity-10 ${user.isActive ? 'text-success' : 'text-danger'}`}>
                    {user.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      size="sm"
                      id="dropdown-basic"
                      title="Perform actions 🙂"
                      className="d-flex align-items-center dropdown-toggle-dark"
                    >
                      Actions
                      {loading[user.email] && (
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="ms-2" />
                      )}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="shadow-sm custom-dropdown-menu">
                      {[
                        {
                          label: user.isActive ? "Deactivate" : "Activate",
                          onClick: () => handleToggleStatus(user.email, user.isActive),
                        },
                        {
                          label: "Release Number",
                          onClick: () => handleActionConfirmation("releaseNumber", user.email),
                        },
                        {
                          label: "Purchase Number",
                          onClick: () => handleActionConfirmation("purchaseNumber", user.email),
                        },
                        {
                          label: "Delete User",
                          onClick: () => handleActionConfirmation("deleteUser", user.email),
                          isDelete: true,
                        },
                      ].map((item, index) => (
                        <Dropdown.Item
                          key={index}
                          onClick={item.onClick}
                          disabled={loading[user.email]}
                          className={`d-flex align-items-center custom-dropdown-item ${
                            item.isDelete ? "delete-item" : ""
                          }`}
                        >
                          {loading[user.email] && (
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                          )}
                          {item.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Pagination */}
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.Prev
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            />
            {Array.from({ length: totalPages }, (_, index) => (
              <Pagination.Item
                key={index + 1}
                active={index + 1 === currentPage}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      </Card>

      {/* Using the separated Confirmation Dialog component */}
      <ConfirmationDialog
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        onConfirm={handleConfirmAction}
        selectedAction={selectedAction}
        selectedEmail={selectedEmail}
        availableNumbers={availableNumbers}
        selectedNumber={selectedNumber}
        setSelectedNumber={setSelectedNumber}
        loading={loading}
      />
    </>
  );
};

export default UserList;